import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as ElementModels from '../../../models/report-view.models';
import { Chart } from 'angular-highcharts';
import { SeriesOptionsType } from 'highcharts';
import { ChartService } from '../../chart/chart.service';
import { getChartColors } from '../panel-utils';
import { map, takeWhile } from 'rxjs';

@Component({
  selector: 'column-chart',
  templateUrl: './column-chart.component.html',
  styleUrls: ['../panel-elements.scss', './column-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ColumnChartComponent implements ElementModels.ElementComponent, OnInit, OnDestroy {
  @Input() dataSet: ElementModels.DataSet;
  @Input() settings: ElementModels.ElementSettings;
  @Input() selectedMetric: string;

  dimensionName: () => string = () => this.settings.dimensionNames[0] || '';
  chartColors: () => string[] = () => getChartColors(this.settings);
  componentActive = true;
  currentChart: Chart;

  constructor(
    private chartService: ChartService
  ) { }

  ngOnInit(): void {
    if (!this.settings.dimensionNames || this.settings.dimensionNames.length === 0) {
      throw new Error('At least one dimension must be available.');
    }

    const dimIndex = this.dataSet.columns.findIndex(col => col.name === this.settings.dimensionNames[0]);
    const dimColumn = this.dataSet.columns[dimIndex];
    const metricIndex = this.dataSet.columns.findIndex(col => col.name === this.selectedMetric);
    if (dimIndex === -1 || metricIndex === -1) {
      throw new Error('Invalid dimension or metric selection.');
    }

    const selectedMetricLabel = this.dataSet.columns[metricIndex].displayName || this.dataSet.columns[metricIndex].name;

    const categories = [];
    const seriesDataMap = new Map();

    this.dataSet.rows.forEach(row => {
      if (!row[dimIndex].value || row[dimIndex].label === ''
          || this.settings.dimensionRowFilters?.some(f => f === row[dimIndex].value || f === row[dimIndex].label)) {
        return;
      }

      const category = row[dimIndex].value;
      const metricValue = row[metricIndex].value;

      if (!categories.includes(category)) {
        categories.push(category);
      }

      seriesDataMap.set(category, {
        name: category,
        y: metricValue,
        displayValue: row[metricIndex].label || metricValue
      });
    });

    const seriesData = Array.from(seriesDataMap.values());

    this.currentChart = this.generateChart(selectedMetricLabel, seriesData, categories);

    this.chartService.reflowChart$.pipe(
      takeWhile(() => this.componentActive),
      map(() => {
        setTimeout(() => {
          this.currentChart.ref.reflow();
        }, 400);
      })
    ).subscribe()

  }

  ngOnDestroy(): void {
    this.componentActive = false;
  }

  private generateChart(selectedMetricLabel: string, seriesData: any[], categories: any[]) {
    const chartSeries: SeriesOptionsType[] = [{
      type: 'column',
      data: seriesData,
      name: selectedMetricLabel
    }];

    var chart = new Chart({
      credits: {
        enabled: false
      },
      title: {
        text: ''
      },
      colors: getChartColors(this.settings),
      chart: {
        type: 'column',
        height: 400
      },
      xAxis: {
        categories: categories, // categories here represent regions
        // Removed datetime-specific properties
      },
      yAxis: {
        title: {
            text: ''
        },
        stackLabels: {
            enabled: false
        }
      },
      tooltip: {
        formatter: function() {
          const point = <any>this.point;
          return `<b>${this.x}</b><br/>${this.series.name}: ${point.displayValue}`;
        },
      },
      plotOptions: {
        series: {
            stacking: 'normal',
            dataLabels: {
              enabled: false
            }
        }
      },
      legend: {
        enabled: true,
        verticalAlign: 'bottom'
      },
      series: chartSeries
    });

    return chart;
  }

}
